import React, { Component } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ScrollTo } from 'react-scroll-to';
import { Button, Divider, Dropdown, Grid, Image, Segment, Header, Modal, Loader } from 'semantic-ui-react';
import { VictoryChart, VictoryGroup, VictoryStack, VictoryAxis, VictoryArea, VictoryPolarAxis, VictoryLabel, VictoryTheme, VictoryTooltip, VictoryVoronoiContainer } from 'victory';

import BeatCard from './BeatCard';

import * as experimentsActions from '../../actions/actionsExperiments';

import dataContent from '../../data/content.json';

// TODO:
//  - Load all initial image emotion data before update charts
//  - Update radar chart, then update area chart

const emotions = [
  'love',
  'smile',
  'surprise',
  'sadness',
  'anxiety',
];

//const emotionLabels = {
//  love: '♥️',
//  smile: '😄',
//  surprise: '😲',
//  sadness: '😢',
//  anxiety: '😬',
//};

const emotionLabels = {
  love: '',
  smile: '',
  surprise: '',
  sadness: '',
  anxiety: '',
};

const style = {
  global: {
    fontSize: '1.5em',
    clickable: {
      cursor: 'pointer'
    }
  },
  card: {
    height: '100%',
    description: {
      fontFamily: 'monospace'
    }
  },
  modal: {
    margin: 'auto',
    height: '450px',
    description: {
      fontSize: '15px'
    }
  },
  divider: {
    width: '100%',
  },
  innerGrid: {
    height: 'calc(100% + 2rem)',
  },
  segment: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchContainerCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  },
  searchInnerContainer: {
    margin: '0 0 auto',
    justifySelf: 'flex-start'
  },
  dropdown: {
    border: '2px solid #2185d0',
    marginTop: '15px'
  },
  step: {
    textDecoration: 'none',
    group: {
      width: '100%'
    }
  }
};

const moodOptions = dataContent['mood-board'].words.positive.concat(dataContent['mood-board'].words.negative).map((word, i) => ({
  key: word,
  text: word,
  value: word,
}));

class MoodBoard extends Component {
  constructor (props) {
    super(props);

    this.updateHeight = this.updateHeight.bind(this);
  }

  // Lifecycle Methods

  componentDidMount() {
    this.updateHeight();
    window.addEventListener('resize', this.updateHeight);

    this.loadSelectedBeatBoard();
  }

  componentDidUpdate(prevProps) {
    const { experiments, match, actions } = this.props;
    const totalBeats = dataContent['mood-board'].beats.length;
    const numLoading = _.values(experiments.beatLoadingStates).filter((obj) => obj.isChartLoading).length;

    if (prevProps.match.params.movieId !== match.params.movieId) {
      this.loadSelectedBeatBoard();
    }

    // If every beat is trying to update, pause component update
    if (numLoading === totalBeats) {
      actions.setPauseUpdate(true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateHeight);
  }

  shouldComponentUpdate(nextProps) {
    const { experiments, actions } = this.props;
    const { pauseUpdate } = experiments;
    const numLoading = _.values(experiments.beatLoadingStates).filter((obj) => obj.isChartLoading).length;
    let doUpdate = !pauseUpdate;

    // If component update is paused, wait until every beat is done loading
    if (pauseUpdate && numLoading === 0) {
      actions.setPauseUpdate(false);
      doUpdate = true;
    }

    return doUpdate;
  }

  // Utility Methods

  getGlobalMaxMin(data) {
    const maxMin = { 
      min: Math.min(..._.keys(data).map((key) => this.getMaxMin(data[key], 'min'))),
      max: Math.max(..._.keys(data).map((key) => this.getMaxMin(data[key], 'max')))
    };

    return {
      min: !isNaN(maxMin.min) ? maxMin.min : -100,
      max: !isNaN(maxMin.max) ? maxMin.max : 100,
    }
  }

  getMaxMin(data, which = 'max') {
    const arr = _.keys(data).filter((key) => emotions.indexOf(key) !== -1).map((key) => data[key]);
    return (which === 'max') ? Math.max(...arr) : Math.min(...arr);
  }

  getNormalizedData(data) {
    const maxValue = this.getMaxMin(data);
    const returnObj = {};

    _.keys(data).filter((key) => emotions.indexOf(key) !== -1).forEach((key) => returnObj[key] = data[key] / (maxValue || 1) * 100);

    return returnObj;
  }

  getAreaData(key) {
    const { beatEmotionData } = this.props.experiments;
    const getBeatData = (beatId) => this.getNormalizedData(beatEmotionData[beatId].data)[key];

    return _.keys(beatEmotionData).map((beatId, i) => ({ x: i, y: getBeatData(beatId) ? getBeatData(beatId) / this.getMaxMin(this.getNormalizedData(beatEmotionData[beatId].data)) - 0.5 : 0 }));
  }

  loadSelectedBeatBoard() {
    const { actions, match } = this.props;
    const movieId = match.params.movieId;
    const beatboard = _.find(dataContent['mood-board'].beatboards, { id: movieId });

    actions.setSelectedMovieId(movieId);

    dataContent['mood-board'].beats.forEach((templateBeat) => {
      actions.setBeatDropdownOptions(templateBeat.id, moodOptions);
    });

    if (!beatboard) {
      dataContent['mood-board'].beats.forEach((templateBeat) => {
        actions.setBeatSelectedPhoto(templateBeat.id, dataContent['mood-board'].images[_.random(0, dataContent['mood-board'].images.length - 1)]);
        actions.setEmotionData(templateBeat.id, { love: 0, smile: 0, surprise: 0, sadness: 0, anxiety: 0 }, true);
        //actions.getPhotos(templateBeat.id, beatValues[templateBeat.id].join('+'));
      });

      dataContent['mood-board'].beats.forEach((templateBeat) => {
        this.loadBeatEmotions(templateBeat.id);
      });
      return;
    }

    dataContent['mood-board'].beats.forEach((templateBeat) => {
      const beat = _.find(beatboard.beats, { id: templateBeat.id });
      actions.setBeatSelectedPhoto(templateBeat.id, beat.imageSrc);
      actions.setEmotionData(templateBeat.id, beat.emotions);
    });
  }

  loadBeatEmotions(beatId) {
    const { actions, experiments } = this.props;
    const { beatSelectedPhotos } = experiments;

    if (beatSelectedPhotos[beatId]) {
      actions.setBeatLoadingStates(beatId, { isChartLoading: true });
      actions.getImageEmotionData(beatId, beatSelectedPhotos[beatId]);

      // DEBUG ONLY
      //actions.setEmotionData(beatId, { love: _.random(0, 100) / 100, smile: _.random(0, 100) / 100, surprise: _.random(0, 100) / 100, sadness: _.random(0, 100) / 100, anxiety: _.random(0, 100) / 100 })
    }
  }

  // Event Handlers

  updateHeight() {
    const { actions } = this.props;

    actions.setBeatContainerHeight(`${window.innerHeight - 50}px`);
  }

  handleChange(beatId, e, { searchQuery, value }) {
    const { actions } = this.props;

    actions.setBeatLoadingStates(beatId, { isPhotoListLoading: true });
    actions.setBeatSearchQuery(beatId, searchQuery, value);
    actions.getPhotos(beatId, value.join('+'));
  }

  handleSearchChange(beatId, e, { searchQuery }) {
    const { actions } = this.props;

    actions.setBeatQuery(beatId, searchQuery);
  }

  handleSearchAddition(beatId, e, { value }) {
    const { actions, experiments } = this.props;
    const { beatDropdownOptions } = experiments;

    actions.setBeatDropdownOptions(beatId, [{ text: value, value }, ...beatDropdownOptions[beatId]]);
    actions.setBeatQuery(beatId, '');
  }

  handleImageClicked(beatId, imageSrc) {
    const { actions } = this.props;

    actions.setBeatSelectedPhoto(beatId, imageSrc);
    setTimeout(() => this.loadBeatEmotions(beatId), 1000);
  }

  handleCalculateEmotionClicked(beatId) {
    this.loadBeatEmotions(beatId);
  }

  capFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  getBeatYPosition(beatId) {
    const rect = document.getElementById(`beatId-${beatId}`).getBoundingClientRect();

    return rect.top - 340 + this.refScrollContainer.scrollTop;
  }

  render() {
    const { experiments } = this.props;
    const { beatLoadingStates, selectedMovieId, beatContainerHeight, beatEmotionData, beatSelectedPhotos, beatDropdownOptions, beatSearchQueries, beatValues, beatPhotos } = experiments;
    const selectedBeatBoard = _.find(dataContent['mood-board'].beatboards, { id: selectedMovieId });
    // const maxMin = this.getGlobalMaxMin(_.keys(beatEmotionData).map((key) => beatEmotionData[key].data));
    const totalBeats = dataContent['mood-board'].beats.length;
    const numLoading = _.values(experiments.beatLoadingStates).filter((obj) => obj.isChartLoading).length;

    return (
      <div className='mood-board' style={{ height: beatContainerHeight }}>
        <div className='movie-selector'>
          <span className='no-align'>Pick an image for every beat of your movie and see how they'll make people feel.</span>
          <Modal dimmer='blurring' size='small' style={style.modal} trigger={<span className='modal-trigger'>About</span>}>
            <Modal.Content>
              <Grid>
                <Grid.Column width={16}>
                  <Modal.Description style={style.modal.description}>
                    <Header>What am I looking at?</Header>
                  </Modal.Description>
                  <br/>
                  <Image wrapped src='/images/moodboard/trajectory-graph.png' />
                  <br/>
                </Grid.Column>
                <Grid.Column width={6}>
                  <Image wrapped src='/images/moodboard/radar-graph.png' />
                </Grid.Column>
                <Grid.Column width={10}>
                  <Modal.Description style={style.modal.description}>
                    <p>
                      This is a tool for mapping a visual story's <strong>emotional trajectory</strong>. 
                    </p>
                    <p>
                      It's a proof-of-concept (based on a famous <a href='https://www.youtube.com/watch?v=GOGru_4z1Vc' target='_blank' rel='noopener noreferrer'>lecture</a> by Kurt Vonnegut and a recent <a href='http://www.uvm.edu/~cdanfort/research/2016-reagan-epj.pdf' target='_blank' rel='noopener noreferrer'>paper</a>) that allows you to see the expected emotional response to given images via an API from <a href='https://emrays.com/image-sentiment-analysis-ai-emotions-api/' target='_blank' rel="noopener noreferrer">Emrays</a>, 
                      a company whose highly developed neural network is the best in the world for calculating the expected emotional response for images and text.
                    </p>
                    <p>
                      By selecting a single mood image for each "beat" of your movie, you can visualize the emotional trajectory of the story 
                      based on the five dimensions captured by the neural network: <strong style={{ color: '#ff6262' }}>love</strong>, <strong style={{ color: '#ff9797' }}>smile</strong>, <strong style={{ color: '#ffb270' }}>surprise</strong>, <strong style={{ color: '#4de4e4' }}>sadness</strong>, and <strong style={{ color: '#47bd47' }}>anxiety</strong>—as
                      well as a compound guess for the overall emotion (provided above each radar graph).
                    </p>
                  </Modal.Description>
                </Grid.Column>
              </Grid>
            </Modal.Content>
          </Modal>
          <div className='button-divider' />
          <ScrollTo>
            {({ scrollTo }) => <Link className='ui button basic primary' to='/experiments/mood-board/' onClick={() => { !selectedMovieId && this.loadSelectedBeatBoard(); scrollTo({ ref: this.refScrollContainer, smooth: true, x: 0, y: 0 }) }}>Load Random Images</Link>}
          </ScrollTo>          
          {/*<div className='button-divider' />
          <Button.Group>
            <Link className={classnames('ui button', { 'primary': selectedMovieId === 'matrix' })} to='/experiments/mood-board/matrix'>The Matrix</Link>
          </Button.Group>
          <span>&nbsp;&nbsp;or check out a pre-made one:&nbsp;&nbsp;&nbsp;</span>
          <Button.Group>
            <Link className={classnames('ui button', { 'primary': selectedMovieId === 'taken' })} to='/experiments/mood-board/taken'>Taken</Link>
            <Link className={classnames('ui button', { 'primary': selectedMovieId === 'toy-story-3' })} to='/experiments/mood-board/toy-story-3'>Toy Story 3</Link>
          </Button.Group>*/}
        </div>
        <div className='chart-container'>
          <VictoryChart
            theme={VictoryTheme.material}
            animate={{ duration: 1000 }}
            width={2500}
            style={{ padding: 0 }}
            containerComponent={<VictoryVoronoiContainer labelComponent={<VictoryTooltip style={{ fontSize: 20 }} />} labels={(point) => `${dataContent['mood-board'].beats[point.x].name} - ${emotionLabels[emotions[point._stack - 1]]} ${this.capFirst(emotions[point._stack - 1])}`} />}
          >
            <VictoryStack colorScale={["#FF9A9A", "#FFE6E6", "#FFDDC0", "#B0E9E9", "#B8F4B8"]}>
              <VictoryArea
                data={this.getAreaData('love')}
                interpolation={"basis"}
              />
              <VictoryArea
                data={this.getAreaData('smile')}
                interpolation={"basis"}
              />
              <VictoryArea
                data={this.getAreaData('surprise')}
                interpolation={"basis"}
              />
              <VictoryArea
                data={this.getAreaData('sadness')}
                interpolation={"basis"}
              />
              <VictoryArea
                data={this.getAreaData('anxiety')}
                interpolation={"basis"}
              />
            </VictoryStack>
            <VictoryAxis
              tickFormat={(t) => Math.round(110 / 14 * t)}
              tickValues={[1.2727, 2.5454, 3.8181, 5.0909, 6.3636, 7.6363, 8.909, 10.1818, 11.4545, 12.7272, 14]}
              style={{ axisLabel: { fontSize: 20, padding: 30 }, tickLabels: { fontSize: 15 }}}
            />
          </VictoryChart>
          {_.values(beatEmotionData).filter((value) => !value.isBaseline).length === 0 && <div className='chart-shrowd'></div>}
          <Grid className='selected-images'>
            <Grid.Column width={1}>
            </Grid.Column>
            <Grid.Column width={14}>
              <Image.Group size='tiny'>
                {_.keys(beatSelectedPhotos).map((key, i) => (
                  <div key={key} className="image-wrapper">
                    <ScrollTo>
                      {({ scrollTo }) => (
                        <Image onClick={() => scrollTo({ ref: this.refScrollContainer, smooth: true, x: 0, y: i !== 0 ? this.getBeatYPosition(key) : 0 })} src={beatSelectedPhotos[key]} />
                      )}
                    </ScrollTo>
                  </div>
                ))}
              </Image.Group>
            </Grid.Column>
            <Grid.Column width={1}>
            </Grid.Column>
          </Grid>
          <div className='chart-label'>Minutes into Film</div>
        </div>
        <div className='scrollable-container' ref={(ref) => this.refScrollContainer = ref}>
          <Grid>
            <Grid.Column width={1}>
            </Grid.Column>
            <Grid.Column width={5} className='beat-header-field'>
              <h3>Movie Story Beats (from <a href='https://www.studiobinder.com/blog/save-the-cat-beat-sheet/' target='_blank' rel='noopener noreferrer'>Save the Cat</a>) {selectedMovieId && selectedMovieId !== 'random' &&  <span>for <strong>{selectedMovieId.split('-').map((w) => this.capFirst(w)).join(' ')}</strong></span>}</h3>
            </Grid.Column>
            <Grid.Column width={5} className='beat-header-field'>
              Selected Mood Image (via <a href='https://www.pexels.com/' target='_blank' rel='noopener noreferrer'>Pexels</a>)
            </Grid.Column>
            <Grid.Column width={4} className='beat-header-field'>
              The feeling evoked by this image (via <a href='https://emrays.com/image-sentiment-analysis-ai-emotions-api/' target='_blank' rel="noopener noreferrer">Emrays</a>)
            </Grid.Column>
            <Grid.Column width={1}>
            </Grid.Column>
            {dataContent['mood-board'].beats.map((beat) => {
              const photos = (beatPhotos[beat.id] || []);

              return (
                <React.Fragment key={beat.id}>
                  <Grid.Column width={1}><div id={`beatId-${beat.id}`} /></Grid.Column>
                  <Grid.Column className='beat-sidebar' width={5}>
                    <BeatCard id={beat.id} name={beat.name} description={selectedMovieId && selectedBeatBoard ? _.find(selectedBeatBoard.beats, { id: beat.id }).notes : beat.description} />
                    {!selectedMovieId && (
                      <React.Fragment>
                        <Image.Group size='tiny' style={style.searchInnerContainer}>
                          {photos.map((photo) => (
                            <Image
                              key={photo.id}
                              className={classnames({ 'selected': beatSelectedPhotos[beat.id] === photo.src.large})}
                              style={style.global.clickable}
                              src={photo.src.small}
                              onClick={this.handleImageClicked.bind(this, beat.id, photo.src.large)}
                              wrapped={true}
                            />
                          ))}
                        </Image.Group>
                        <Dropdown
                          style={style.dropdown}
                          fluid
                          loading={(beatLoadingStates[beat.id] || {}).isPhotoListLoading}
                          multiple
                          closeOnChange
                          allowAdditions
                          onChange={this.handleChange.bind(this, beat.id)}
                          onSearchChange={this.handleSearchChange.bind(this, beat.id)}
                          onAddItem={this.handleSearchAddition.bind(this, beat.id)}
                          options={beatDropdownOptions[beat.id] || []}
                          placeholder='Change selected image'
                          search
                          searchQuery={beatSearchQueries[beat.id]}
                          selection
                          value={beatValues[beat.id] || []}
                        />
                      </React.Fragment>
                    )}
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <Segment>
                      <Image src={beatSelectedPhotos[beat.id]} style={style.searchInnerContainer}/>  
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Segment className={classnames('data-sidebar', { 'has-data': !(beatEmotionData[beat.id] || {}).isBaseline && !(beatLoadingStates[beat.id] || {}).isChartLoading })}>
                      {numLoading === totalBeats && <Loader size='large' active={(beatLoadingStates[beat.id] || {}).isChartLoading} />}
                      {!selectedMovieId && (beatEmotionData[beat.id] || {}).isBaseline && !(beatLoadingStates[beat.id] || {}).isChartLoading && 
                        <Button 
                          primary
                          loading={(beatLoadingStates[beat.id] || {}).isChartLoading}
                          disabled={!beatSelectedPhotos[beat.id] || (beatLoadingStates[beat.id] || {}).isChartLoading}
                          onClick={this.handleCalculateEmotionClicked.bind(this, beat.id)}
                        >
                          Chart Emotion of Selected Image
                        </Button>
                      }
                      <Segment>
                        <div className='emotion-label'>{((beatEmotionData[beat.id] || {}).data || {}).compound ? this.capFirst(((beatEmotionData[beat.id] || {}).data || {}).compound) : <React.Fragment>&nbsp;</React.Fragment>}</div>
                        <VictoryChart polar theme={VictoryTheme.material} domain={{ y: [0, 100] }} animate={{ duration: 1000 }}>
                          <VictoryGroup colorScale={"blue"} style={{ data: { fillOpacity: 0.2, strokeWidth: 2 } }}>
                            <VictoryArea 
                              data={[
                                { x: 'love', y: (((beatEmotionData[beat.id] || {}).data) || {}).love * 100 },
                                { x: 'smile', y: (((beatEmotionData[beat.id] || {}).data) || {}).smile * 100 },
                                { x: 'surprise', y: (((beatEmotionData[beat.id] || {}).data) || {}).surprise * 100 },
                                { x: 'sadness', y: (((beatEmotionData[beat.id] || {}).data) || {}).sadness * 100 },
                                { x: 'anxiety', y: (((beatEmotionData[beat.id] || {}).data) || {}).anxiety * 100 }
                              ]}
                              interpolation='cardinal'
                              style={{ data: { fill: "#002c61", fillOpacity: 0.2, stroke: "#002c61", strokeOpacity: 0.75 } }}
                            />
                          </VictoryGroup>
                          {_.keys(emotionLabels).map((key, i) => (
                            <VictoryPolarAxis key={key} dependentAxis
                              style={{
                                axis: { 
                                  stroke: () => ({ love: '#FF9A9A', smile: '#FFE6E6', surprise: '#FFDDC0', sadness: '#B0E9E9', anxiety: '#B8F4B8' }[key]), 
                                  strokeWidth: 10, 
                                  opacity: 0.5
                                },
                                tickLabels: { fontSize: 12, padding: 15, opacity: 0.35 },
                                axisLabel: { padding: 20, fontSize: 15, fill: '#999' },
                                grid: { stroke: "grey", strokeWidth: 0.25, opacity: 0.5 }
                              }}
                              tickLabelComponent={
                                <VictoryLabel labelPlacement="vertical"/>
                              }
                              labelPlacement="perpendicular"
                              axisValue={i + 1}
                              label={`${emotionLabels[key]} ${this.capFirst(key)}`}
                              tickFormat={(t) => t}
                              tickValues={[25, 50, 75]}
                              innerRadius={100}
                            />
                          ))}
                          </VictoryChart>
                        </Segment>
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={1}></Grid.Column>
                  <Divider style={style.divider} />
                </React.Fragment>
              );
            })}
          </Grid>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  return {
    experiments: state.experiments
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(experimentsActions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MoodBoard);



// WEBPACK FOOTER //
// ./src/components/experiments/MoodBoard.js